import React from 'react';

import { css, cx } from '@emotion/css';

import { useStyles } from '@/hooks/useStyles.ts';
import { BaseLoaderProps, Loader } from '@/uiKit/atoms/pending/Loader.tsx';
import { AtomProps, StyleClasses } from '@/uiKit/atoms/utils/AtomTypes.ts';

export type BaseCenteredLoaderProps = Partial<BaseLoaderProps>;

export type CenteredLoaderClasses = StyleClasses<'root' | 'loader'>;

export type CenteredLoaderProps = AtomProps<
  BaseCenteredLoaderProps,
  CenteredLoaderClasses
>;

/**
 * CenteredLoader is a functional React component that displays a centralized loading spinner.
 *
 * Very similar to the Loader component, but with a centered layout.
 *
 * The loader can be customized with the following props:
 * - size: specifies the size of the loader. Can accept predefined size keys.
 * - thickness: determines the thickness of the loader. Defaults to 1.
 * - color: defines the color of the loader. Defaults to 'primary'.
 */
export const CenteredLoader: React.FC<CenteredLoaderProps> = ({
  size = 'L',
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <div className={cx(styles.container, className, classes?.root)}>
      <Loader {...props} className={classes?.loader} size={size} />
    </div>
  );
};

const makeStyles = () => ({
  container: css({
    width: '100%',
    height: '100%',
    flexGrow: 1,
    flexShrink: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
});
